import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { usePeriodStore } from '@/stores/period.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, DateInput, FormField, Textarea } from '@/components/ui/Input.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/Dialog.jsx';
import { Badge } from '@/components/ui/Badge.jsx';
import { DataTable } from '@/components/DataTable.jsx';
import { EmployeePicker } from '@/components/EmployeePicker.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { formatDate, formatHours, monthName } from '@/lib/format.js';
import { Plus, Check, X, Trash2, Zap } from 'lucide-react';

export default function ShortLeavePage() {
  const { year, month } = usePeriodStore();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ employee_id: '', date: new Date().toISOString().slice(0, 10), hours: 1, reason: '' });
  const [autoOpen, setAutoOpen] = useState(false);
  const [preview, setPreview] = useState(null);

  const queryKey = ['shortLeave', { year, month }];
  const { data = [], isLoading } = useQuery({ queryKey, queryFn: () => api.get(`/short-leave?year=${year}&month=${month}`) });

  const create = useMutation({
    mutationFn: () => api.post('/short-leave', { ...draft, employee_id: Number(draft.employee_id), hours: Number(draft.hours) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey }); setOpen(false); toast({ variant: 'success', title: 'Created' }); },
    onError: (e) => toast({ variant: 'error', title: 'Failed', description: e.message }),
  });
  const decide = useMutation({ mutationFn: ({ id, status }) => api.post(`/short-leave/${id}/decision`, { status }), onSuccess: () => qc.invalidateQueries({ queryKey }) });
  const del = useMutation({ mutationFn: (id) => api.del(`/short-leave/${id}`), onSuccess: () => qc.invalidateQueries({ queryKey }) });

  const autoPreview = useMutation({
    mutationFn: () => api.post('/short-leave/auto-detect', { year: Number(year), month: Number(month), dryRun: true }),
    onSuccess: (data) => { setPreview(data); setAutoOpen(true); },
    onError: (e) => toast({ variant: 'error', title: 'Auto-detect failed', description: e.message }),
  });
  const autoConfirm = useMutation({
    mutationFn: () => api.post('/short-leave/auto-detect', { year: Number(year), month: Number(month), dryRun: false }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey });
      setAutoOpen(false);
      setPreview(null);
      toast({ variant: 'success', title: `Auto-detected: ${data.created} created, ${data.skipped} skipped` });
    },
    onError: (e) => toast({ variant: 'error', title: 'Auto-detect failed', description: e.message }),
  });

  const columns = [
    { key: 'date', header: 'Date', sortable: true, render: (r) => formatDate(r.date), className: 'font-mono' },
    { key: 'emp_no', header: 'Emp No', className: 'font-mono', sortable: true },
    { key: 'full_name', header: 'Name', sortable: true },
    { key: 'hours', header: 'Hours', className: 'text-right tabular-nums font-mono', headClassName: 'text-right', render: (r) => formatHours(r.hours) },
    { key: 'reason', header: 'Reason', className: 'font-mono' },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'}>{r.status}</Badge> },
    { key: 'consumed', header: 'Consumed', render: (r) => r.consumed_in_payroll_run_id ? <Badge variant="info">Payroll #{r.consumed_in_payroll_run_id}</Badge> : null },
    { key: '__a', header: '', className: 'w-32 text-right',
      render: (r) => (
        <div className="flex justify-end gap-1">
          {r.status === 'pending' && !r.consumed_in_payroll_run_id && <>
            <Button variant="ghost" size="icon" onClick={() => decide.mutate({ id: r.id, status: 'approved' })}><Check className="h-4 w-4 text-emerald-600" /></Button>
            <Button variant="ghost" size="icon" onClick={() => decide.mutate({ id: r.id, status: 'rejected' })}><X className="h-4 w-4 text-red-500" /></Button>
          </>}
          {!r.consumed_in_payroll_run_id && <Button variant="ghost" size="icon" onClick={() => del.mutate(r.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
        </div>
      ) },
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Short Leave" description={`${monthName(month)} ${year}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => autoPreview.mutate()} disabled={autoPreview.isPending}><Zap className="h-4 w-4" />Auto Detect</Button>
            <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" />New Short Leave</Button>
          </div>
        }
      />
      <Section><DataTable columns={columns} data={data} loading={isLoading} exportFilename={`short-leave-${year}-${month}.csv`} /></Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Short Leave</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <FormField label="Employee" required><EmployeePicker value={draft.employee_id} onChange={(v) => setDraft({ ...draft, employee_id: v })} /></FormField>
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Date" required><DateInput value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></FormField>
              <FormField label="Hours"><Input type="number" step="0.25" value={draft.hours} onChange={(e) => setDraft({ ...draft, hours: e.target.value })} /></FormField>
            </div>
            <FormField label="Reason"><Textarea value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} /></FormField>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => create.mutate()} disabled={!draft.employee_id || create.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={autoOpen} onOpenChange={(v) => { setAutoOpen(v); if (!v) setPreview(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Auto Detect Short Leave — {monthName(month)} {year}</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3">
              <div className="flex gap-4 text-sm">
                <span className="text-emerald-600 font-medium">{preview.willCreate} to create</span>
                <span className="text-muted-foreground">{preview.skipped} already exist (skipped)</span>
              </div>
              {preview.willCreate === 0 ? (
                <p className="text-sm text-muted-foreground">No new shortfalls detected from attendance data.</p>
              ) : (
                <div className="max-h-72 overflow-y-auto rounded border text-sm">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-muted/80">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Date</th>
                        <th className="px-3 py-2 text-left font-medium">Emp No</th>
                        <th className="px-3 py-2 text-left font-medium">Name</th>
                        <th className="px-3 py-2 text-right font-medium">Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-1.5 font-mono">{formatDate(r.date)}</td>
                          <td className="px-3 py-1.5 font-mono">{r.emp_no}</td>
                          <td className="px-3 py-1.5">{r.full_name}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums font-mono">{formatHours(r.hours)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
            <Button onClick={() => autoConfirm.mutate()} disabled={!preview || preview.willCreate === 0 || autoConfirm.isPending}>
              Create {preview?.willCreate ?? ''} Records
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
